import { useCallback, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { v2Client } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";
import { NO_CHANNELS_SELECTED } from "./channelFilter";
import type { HighlightClip } from "../Highlights/types";
import type { ContentItem } from "./types";

/** WHIZZARD_TYPE keys the library can filter on (see Highlights/types). */
export const WHIZZARD_TYPES = { condensed: 3, goals: 2 } as const;
export type WhizzardTypeFilter = "all" | keyof typeof WHIZZARD_TYPES;

export const WHIZZARD_TYPE_LABEL: Record<WhizzardTypeFilter, string> = {
  all: "All types",
  condensed: "Condensed games",
  goals: "Goal of the day",
};

const PAGE_SIZE = 24;
/** A whizzard with no recorded runtime (a few goals clips) is placed at this
 * length and flagged as an estimate, like a game before its probe. */
const ASSUMED_WHIZZARD_SEC = 5 * 60;

function authHeaders(): Record<string, string> {
  const token = getCurrentAuth()?.AccessToken;
  return token ? { Authorization: `bearer ${token}` } : {};
}

/** A whizzard as a library item. "wz-" is the id prefix the save path turns
 * into contentType "whizzard" (see apiMapping), and a whizzard stays on its
 * own channel, the same rule games follow. */
export function whizzardToContent(clip: HighlightClip): ContentItem {
  const seconds = clip.Duration ? Math.round(clip.Duration) : 0;
  return {
    id: `wz-${clip.ID}`,
    title: clip.Title || clip.Description || `Highlight ${clip.ID}`,
    type: "whizzard",
    league: "",
    thumbnail: clip.Thumbnail ?? "",
    durationSec: seconds || ASSUMED_WHIZZARD_SEC,
    durationEstimated: !seconds,
    date: (clip.createdAt || clip.HighlightDate || "").slice(0, 10),
    channelId: clip.ChannelID != null ? String(clip.ChannelID) : undefined,
    source: clip as unknown as Record<string, unknown>,
  };
}

/**
 * The Whizzards tab's feed: /v2/getWhizzards (the Highlights page's API),
 * filtered on the server by channel, type and search and paged as the grid
 * scrolls. `channelValue` must already be scoped to the supported channels
 * (scopeToChannels), so the feed never returns another channel's clips.
 */
export function useWhizzards(channelValue: string, search: string, typeFilter: WhizzardTypeFilter) {
  const enabled = channelValue !== NO_CHANNELS_SELECTED && channelValue !== "";
  const query = useInfiniteQuery({
    queryKey: ["v2", "channel-schedule", "whizzards", channelValue, search, typeFilter],
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const params: Record<string, string | number> = {
        page: pageParam,
        limit: PAGE_SIZE,
        channel_id: channelValue,
      };
      if (typeFilter !== "all") params.type = WHIZZARD_TYPES[typeFilter];
      if (search) params.search = search;
      const res = await v2Client.get<{ data?: { wizzards?: HighlightClip[] } }>(APIRoutes.GetWhizzardsV2, {
        params,
        headers: authHeaders(),
        signal,
      });
      return res.data?.data?.wizzards ?? [];
    },
    // A full page means there is probably another; a short one ends it.
    getNextPageParam: (last, all) => (last.length >= PAGE_SIZE ? all.length + 1 : undefined),
  });

  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: ContentItem[] = [];
    for (const clip of query.data?.pages.flat() ?? []) {
      if (seen.has(String(clip.ID))) continue;
      seen.add(String(clip.ID));
      out.push(whizzardToContent(clip));
    }
    return out;
  }, [query.data]);

  const { fetchNextPage: fetchNext } = query;
  const fetchNextPage = useCallback(() => void fetchNext(), [fetchNext]);

  return {
    items,
    isLoading: enabled && query.isLoading,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage,
  };
}

export type WhizzardsState = ReturnType<typeof useWhizzards>;
